import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { setFavicon } from '../utils/favicon';
import { setDeviseCourante } from '../utils/format';

// La console Super Admin (/superadmin/*) a sa propre identité : on ne lui
// impose JAMAIS le thème ni le favicon d'un commerce mis en cache.
const surConsoleSuperAdmin = () =>
  (typeof window !== 'undefined' && (window.location.pathname || '').startsWith('/superadmin'));

// Exporté pour que des composants bas-niveau (ex : InputMontant) puissent lire
// le contexte sans passer par useAuth (qui lève une erreur hors provider).
export const AuthContext = createContext(null);

// Délais d'inactivité avant fermeture automatique de la session (en ms).
// L'admin du commerce (compte sensible) est fermé plus vite ; les autres
// comptes (propriétaire, gérant, vendeur, consultation) après 15 minutes.
const INACTIVITE_ADMIN_MS  = 5  * 60 * 1000; // 5 minutes
const INACTIVITE_AUTRES_MS = 15 * 60 * 1000; // 15 minutes

const COULEUR_FOND_DEFAUT = '#1e3a8a';   // bleu marine
const COULEUR_ICONE_DEFAUT = '#64748b';  // gris ardoise
const CACHE_KEY = 'sv_cached_commerce';

// Anciennes couleurs par défaut historiques qu'on remplace silencieusement
// par le nouveau défaut bleu marine. Évite que la sidebar s'affiche en noir
// pour les commerces dont la valeur a été mise à un fond très sombre par
// l'ancien thème ou un cache obsolète.
const COULEURS_LEGACY = new Set([
  '#eab308',  // jaune (ancien défaut)
  '#000000', '#000',
  '#030712', '#0f172a', '#1a1a1a', '#111827', '#1f2937',
]);
function couleurFondEffective(hex) {
  if (!hex) return COULEUR_FOND_DEFAUT;
  return COULEURS_LEGACY.has(String(hex).toLowerCase())
    ? COULEUR_FOND_DEFAUT
    : hex;
}

// Lit / écrit le cache du commerce dans localStorage.
// Sert à appliquer le thème (logo, couleur, nom) AVANT que /auth/me réponde,
// pour éviter le flash visuel "Soul Valise" au refresh.
function readCachedCommerce() {
  try {
    return JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
  } catch {
    return null;
  }
}
function writeCachedCommerce(c) {
  try {
    if (c) {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        nom: c.nom, logo_url: c.logo_url,
        couleur_principale: c.couleur_principale,
        couleur_secondaire: c.couleur_secondaire,
        devise: c.devise,
      }));
    } else {
      localStorage.removeItem(CACHE_KEY);
    }
  } catch {}
}

// Calcule l'URL absolue d'un logo /uploads/... (sinon renvoie tel quel).
function resolveLogoUrl(logo_url) {
  if (!logo_url) return null;
  if (logo_url.startsWith('http')) return logo_url;
  if (logo_url.startsWith('/uploads/')) {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
      .replace(/\/api\/?$/, '');
    return base + logo_url;
  }
  return logo_url;
}

// Convertit #RRGGBB en composantes RGB (utile pour fabriquer des variations
// transparentes en runtime sans recompiler Tailwind).
function hexToRgb(hex) {
  if (!hex) return null;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return null;
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

// Calcule la luminance perçue d'une couleur hex et renvoie le texte
// le plus lisible (blanc ou noir) à utiliser par-dessus.
function textColorFor(bg) {
  const rgb = hexToRgb(bg);
  if (!rgb) return '#ffffff';
  // Luminance perçue (formule W3C / coefficients YIQ)
  const lum = (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
  return lum > 0.55 ? '#0f172a' : '#ffffff';
}

// Applique les 2 couleurs du commerce (fond + icônes) comme CSS variables
// sur :root, plus une couleur de texte auto-contrastée par-dessus le fond.
function appliquerTheme(couleurFond, couleurIcones) {
  const root = document.documentElement;
  const bg   = couleurFondEffective(couleurFond);
  const icon = couleurIcones || COULEUR_ICONE_DEFAUT;
  const textOnBg = textColorFor(bg);

  // Couleur principale = fond
  root.style.setProperty('--sv-bg',          bg);
  root.style.setProperty('--sv-text-on-bg',  textOnBg);
  const rgbBg = hexToRgb(bg);
  if (rgbBg) {
    root.style.setProperty('--sv-bg-rgb',     `${rgbBg.r}, ${rgbBg.g}, ${rgbBg.b}`);
    root.style.setProperty('--sv-bg-soft',    `rgba(${rgbBg.r}, ${rgbBg.g}, ${rgbBg.b}, 0.15)`);
    root.style.setProperty('--sv-bg-border',  `rgba(${rgbBg.r}, ${rgbBg.g}, ${rgbBg.b}, 0.3)`);
  }
  // Couleur secondaire = icônes/accents
  root.style.setProperty('--sv-icon',          icon);
  const rgbIc = hexToRgb(icon);
  if (rgbIc) {
    root.style.setProperty('--sv-icon-rgb',    `${rgbIc.r}, ${rgbIc.g}, ${rgbIc.b}`);
    root.style.setProperty('--sv-icon-soft',   `rgba(${rgbIc.r}, ${rgbIc.g}, ${rgbIc.b}, 0.15)`);
    root.style.setProperty('--sv-icon-border', `rgba(${rgbIc.r}, ${rgbIc.g}, ${rgbIc.b}, 0.3)`);
  }
  // Rétro-compatibilité avec --sv-accent (utilisé avant cette migration)
  root.style.setProperty('--sv-accent',        icon);
  root.style.setProperty('--sv-accent-soft',   `rgba(${rgbIc?.r||100}, ${rgbIc?.g||116}, ${rgbIc?.b||139}, 0.15)`);
  root.style.setProperty('--sv-accent-border', `rgba(${rgbIc?.r||100}, ${rgbIc?.g||116}, ${rgbIc?.b||139}, 0.3)`);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Initialiser commerce DEPUIS le cache pour appliquer le thème immédiatement
  const [commerce, setCommerce] = useState(() => {
    const c = readCachedCommerce();
    // Devise dès le 1er rendu (formatMontant l'utilise partout).
    if (c?.devise) setDeviseCourante(c.devise);
    return c;
  });
  const [loading, setLoading] = useState(true);
  const [modulesActifs, setModulesActifs] = useState(null);

  // Garder la devise d'affichage (formatMontant) alignée sur le commerce chargé.
  useEffect(() => { setDeviseCourante(commerce?.devise); }, [commerce]);

  // Appliquer le thème dès le mount depuis le cache (avant même /auth/me).
  // Sauf sur la console Super Admin, qui garde son identité neutre.
  useEffect(() => {
    if (surConsoleSuperAdmin()) return;
    const cached = readCachedCommerce();
    appliquerTheme(cached?.couleur_principale, cached?.couleur_secondaire);
    setFavicon(resolveLogoUrl(cached?.logo_url));
  }, []);

  // Bootstrap depuis /auth/me (charge aussi le commerce + thème)
  useEffect(() => {
    const token = localStorage.getItem('sv_access_token');
    if (token) {
      api.get('/auth/me')
        .then(res => {
          setUser(res.data.user);
          setCommerce(res.data.commerce || null);
          writeCachedCommerce(res.data.commerce || null);
          if (!surConsoleSuperAdmin()) {
            appliquerTheme(res.data.commerce?.couleur_principale, res.data.commerce?.couleur_secondaire);
            setFavicon(resolveLogoUrl(res.data.commerce?.logo_url));
          }
        })
        .catch(() => {
          localStorage.removeItem('sv_access_token');
          localStorage.removeItem('sv_refresh_token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!user) { setModulesActifs(null); return; }
    api.get('/mes-modules')
      .then(res => setModulesActifs(res.data.modules_actifs || []))
      .catch(() => setModulesActifs([]));
  }, [user]);

  // ── Synchronisation entre onglets ──────────────────────────────
  // Le token est partagé via localStorage : si l'utilisateur se connecte
  // à un AUTRE commerce dans un second onglet, le token de cet onglet-ci
  // est écrasé. Sans ce listener, l'onglet affichait un mélange déroutant
  // (interface du commerce A, données du commerce B). On recharge la page
  // pour adopter proprement la nouvelle session — ou l'écran de connexion
  // si l'autre onglet s'est déconnecté.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'sv_access_token' && e.oldValue !== e.newValue) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // Stocke la session (tokens) puis charge le profil complet + thème + cache.
  const etablirSession = async ({ accessToken, refreshToken, user }) => {
    // Nouvelle session → on repart sur la vue « toutes les boutiques »
    localStorage.removeItem('sv_boutique_vue');
    localStorage.setItem('sv_access_token', accessToken);
    localStorage.setItem('sv_refresh_token', refreshToken);
    setUser(user);
    // Charger immédiatement le commerce + appliquer le thème + cache
    try {
      const me = await api.get('/auth/me');
      // /me renvoie le profil COMPLET (photo_url, téléphone…) que le login
      // n'inclut pas → on remplace le user minimal pour que la photo
      // s'affiche tout de suite, sans recharger la page.
      if (me.data.user) setUser(me.data.user);
      setCommerce(me.data.commerce || null);
      writeCachedCommerce(me.data.commerce || null);
      appliquerTheme(me.data.commerce?.couleur_principale, me.data.commerce?.couleur_secondaire);
      setFavicon(resolveLogoUrl(me.data.commerce?.logo_url));
    } catch {}
    return user;
  };

  const login = async (identifiant, password) => {
    const res = await api.post('/auth/login', { identifiant, password });
    // 2FA active : le backend ne renvoie pas de session mais demande un code.
    // On ne stocke rien et on remonte l'info à la page de connexion.
    if (res.data?.mfaRequired) {
      return { mfaRequired: true, challengeToken: res.data.challengeToken };
    }
    return etablirSession(res.data);
  };

  // Deuxième étape quand la 2FA est active : échange challengeToken + code.
  const login2fa = async (challengeToken, code) => {
    const res = await api.post('/auth/login/2fa', { challengeToken, code });
    return etablirSession(res.data);
  };

  const logout = async () => {
    // La vue boutique sélectionnée ne doit pas survivre à la session
    localStorage.removeItem('sv_boutique_vue');
    const refreshToken = localStorage.getItem('sv_refresh_token');
    try {
      await api.post('/auth/logout', { refreshToken });
    } catch (_) {}
    localStorage.removeItem('sv_access_token');
    localStorage.removeItem('sv_refresh_token');
    writeCachedCommerce(null);   // vider le cache identité
    try { localStorage.removeItem('sv_cached_abo'); } catch {}
    setUser(null);
    setCommerce(null);
    appliquerTheme(null, null);
    setFavicon(null);
  };

  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      setUser(res.data.user);
      setCommerce(res.data.commerce || null);
      writeCachedCommerce(res.data.commerce || null);
      appliquerTheme(res.data.commerce?.couleur_principale, res.data.commerce?.couleur_secondaire);
      setFavicon(resolveLogoUrl(res.data.commerce?.logo_url));
      return res.data.user;
    } catch {
      return null;
    }
  };

  // ── Time-out d'inactivité ───────────────────────────────────────────────
  // Tant qu'un utilisateur est connecté, on arme un minuteur. La moindre
  // action (souris, clavier, clic, scroll, toucher) le réarme. Sans action
  // pendant le délai (5 min pour l'admin, 15 min pour les autres comptes),
  // la session est fermée automatiquement.
  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  useEffect(() => {
    if (!user) return;
    const delai = user.role === 'admin' ? INACTIVITE_ADMIN_MS : INACTIVITE_AUTRES_MS;
    const minutes = Math.round(delai / 60000);

    let timer;
    const fermer = () => {
      // Drapeau lu par la page de connexion pour afficher « session expirée ».
      try { sessionStorage.setItem('sv_session_expiree', '1'); } catch { /* ignore */ }
      logoutRef.current();
      toast(`Session fermée après ${minutes} minutes d'inactivité.`, { icon: '🔒', duration: 6000 });
    };
    const rearmer = () => {
      clearTimeout(timer);
      timer = setTimeout(fermer, delai);
    };

    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, rearmer, { passive: true }));
    rearmer(); // démarre le compte à rebours

    return () => {
      clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, rearmer));
    };
  }, [user]);

  const isAdmin = user?.role === 'admin';
  const isProprietaire = ['admin', 'proprietaire'].includes(user?.role);
  const isStaff = ['admin', 'proprietaire', 'gerant_vendeur'].includes(user?.role);
  // Profil « consultation » : lecture seule totale (aucune écriture, ni métier
  // ni paramètres). Les helpers canEdit* l'excluent donc volontairement.
  const isConsultation = user?.role === 'consultation';
  const canEditBusiness = ['proprietaire', 'gerant_vendeur'].includes(user?.role);
  // Catalogue (produits + fournisseurs) : l'admin du commerce peut créer/éditer,
  // EN PLUS du propriétaire et du gérant. (Doit refléter le middleware backend
  // `writeCatalog`.)
  const canEditCatalog = ['admin', 'proprietaire', 'gerant_vendeur'].includes(user?.role);
  const canEditParametres = ['admin', 'proprietaire'].includes(user?.role);
  const canManageUsers = user?.role === 'admin';
  const mustChangePassword = user?.doit_changer_mot_de_passe === true;

  const moduleActif = (code) => {
    if (!code) return true;
    if (modulesActifs === null) return true;
    if (Array.isArray(modulesActifs) && modulesActifs.length === 0) return true;
    return modulesActifs.includes(code);
  };

  // Helpers thème exposés au reste de l'app
  // couleurFondEffective() filtre les valeurs historiques (jaune, noir) et
  // les remplace par le nouveau défaut bleu marine pour que la sidebar
  // ne soit jamais noire par défaut.
  const couleurFond    = couleurFondEffective(commerce?.couleur_principale);
  const couleurIcones  = commerce?.couleur_secondaire  || COULEUR_ICONE_DEFAUT;
  const couleurTexte   = textColorFor(couleurFond);
  // Compat ascendante : couleurAccent = ancien nom de la couleur icones
  const couleurAccent  = couleurIcones;
  const logoUrl = resolveLogoUrl(commerce?.logo_url);
  const nomCommerce = commerce?.nom || '';

  return (
    <AuthContext.Provider value={{
      user, loading, login, login2fa, logout, refreshUser,
      isAdmin, isProprietaire, isStaff, isConsultation,
      canEditBusiness, canEditCatalog, canEditParametres, canManageUsers,
      mustChangePassword,
      modulesActifs, moduleActif,
      commerce, logoUrl, nomCommerce,
      couleurFond, couleurIcones, couleurTexte, couleurAccent,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans AuthProvider');
  return ctx;
};
